import React, { forwardRef } from 'react';
import { observer } from 'mobx-react';
import { FormPermissionProvider } from './FormPermissionProvider';
import { FormSchemaProvider } from './FormSchemaProvider';
import { FormDefaultValuesProvider } from './FormDefaultValuesProvider';
import FormAccessEvaluator from './FormAccessEvaluator';
import { FormComposerProvider } from './FormComposerProvider';
import FormRenderer from './FormRenderer';
import FormFieldGenerator from './FormFieldGenerator';
import { FormDebugProvider } from './FormDebugProvider';
import { FormValidationProvider } from './FormValidationProvider';
import FormDebugWindow from './debugTools/FormDebugWindow';

const FORCE_DEBUG = false;
const FORCE_DEBUG_STEPS_ONLY = false;

const Form = forwardRef(
  (
    {
      formId,
      hasAccess = null,
      permissionEntities = null,
      permittedByDefault = false,
      schema,
      fetchSchema,
      validationSchema = null,
      fetchValidationSchema = null,
      nonSchemaValidations = null,
      defaultValues = null,
      fetchDefaultValues = null,
      customComponents,
      dialogs,
      children,
      onSubmit,
      debug = false,
      debugStepsOnly = false,
      submitWithReadOnlyFields = false,
      dialog = false,
      staticPermissions = null,
      submitWithoutUpdates = false,
    },
    ref,
  ) => {
    if (!formId) {
      throw new Error('User Access parameter needed in Form');
    }

    if (!onSubmit) {
      throw new Error('onSubmit parameter needed in Form');
    }

    return (
      <FormDebugProvider
        debug={FORCE_DEBUG || debug}
        debugStepsOnly={FORCE_DEBUG_STEPS_ONLY || debugStepsOnly}>
        <FormAccessEvaluator hasAccess={hasAccess}>
          <FormPermissionProvider
            staticPermissions={staticPermissions}
            permissionEntities={permissionEntities}
            permittedByDefault={permittedByDefault}>
            <FormSchemaProvider schema={schema} fetchSchema={fetchSchema}>
              <FormValidationProvider
                validationSchema={validationSchema}
                fetchValidationSchema={fetchValidationSchema}>
                <FormDefaultValuesProvider
                  defaultValues={defaultValues}
                  fetchDefaultValues={fetchDefaultValues}>
                  <FormComposerProvider
                    onSubmit={onSubmit}
                    formId={formId}
                    validationSchema={validationSchema}>
                    <FormRenderer
                      submitWithoutUpdates={submitWithoutUpdates}
                      nonSchemaValidations={nonSchemaValidations || null}
                      dialog={dialog}
                      submitWithReadOnlyFields={submitWithReadOnlyFields}
                      ref={ref}>
                      <div className="form-fields">
                        <FormFieldGenerator
                          customComponents={customComponents}
                          dialogs={dialogs}
                        />
                        {children}
                      </div>
                      <FormDebugWindow />
                    </FormRenderer>
                  </FormComposerProvider>
                </FormDefaultValuesProvider>
              </FormValidationProvider>
            </FormSchemaProvider>
          </FormPermissionProvider>
        </FormAccessEvaluator>
      </FormDebugProvider>
    );
  },
);

export default observer(Form);
